import { BUILD_VERSION } from "../versioning/version";

export const useGetAnalyticsData = () => {
  const getAnalyticsData = () => {
    const isElectron = window.navigator.userAgent.indexOf("Electron") !== -1;
    const appType = isElectron ? "desktop" : "web";
    const appVersion = isElectron
      ? (BUILD_VERSION?.releaseVersion ?? "unknown")
      : (BUILD_VERSION?.version ?? "unknown");

    // Get OS version and details
    const userAgent = navigator.userAgent;
    const osInfo = getOSInfo(userAgent);

    return {
      appVersion,
      appType,
      os: osInfo.os,
      osVersion: osInfo.version,
      ...(appType === "web" && { browser: getBrowserInfo(userAgent) }),
      ...(isElectron && { electronVersion: getElectronVersion(userAgent) }),
    };
  };

  return getAnalyticsData;
};

/**
 * Extract OS information from user agent
 */
const getOSInfo = (userAgent: string): { os: string; version: string } => {
  const ua = userAgent.toLowerCase();

  // Windows
  if (ua.indexOf("windows") !== -1) {
    const match = ua.match(/windows nt ([\d.]+)/);
    const ntVersion = match ? match[1] : "unknown";
    const windowsVersion = mapWindowsNTVersion(ntVersion);
    return { os: "Windows", version: windowsVersion };
  }

  // macOS
  if (ua.indexOf("mac os x") !== -1) {
    const match = ua.match(/mac os x ([\d_]+)/);
    const version = match ? match[1].replace(/_/g, ".") : "unknown";
    return { os: "macOS", version };
  }

  // Linux
  if (ua.indexOf("linux") !== -1) {
    const isAndroid = ua.indexOf("android") !== -1;
    if (isAndroid) {
      const match = ua.match(/android ([\d.]+)/);
      return { os: "Android", version: match ? match[1] : "unknown" };
    }
    return { os: "Linux", version: "unknown" };
  }

  // iOS
  if (ua.indexOf("iphone") !== -1 || ua.indexOf("ipad") !== -1) {
    const match = ua.match(/os ([\d_]+)/);
    const version = match ? match[1].replace(/_/g, ".") : "unknown";
    return { os: "iOS", version };
  }

  return { os: "unknown", version: "unknown" };
};

/**
 * Map Windows NT version to friendly name
 */
const mapWindowsNTVersion = (ntVersion: string): string => {
  const versionMap: Record<string, string> = {
    "10.0": "10/11",
    "6.3": "8.1",
    "6.2": "8",
    "6.1": "7",
    "6.0": "Vista",
  };
  return versionMap[ntVersion] || ntVersion;
};

/**
 * Extract browser information
 */
const getBrowserInfo = (userAgent: string): string => {
  const ua = userAgent.toLowerCase();

  if (ua.indexOf("edg") !== -1) return "Edge";
  if (ua.indexOf("chrome") !== -1) return "Chrome";
  if (ua.indexOf("safari") !== -1) return "Safari";
  if (ua.indexOf("firefox") !== -1) return "Firefox";
  if (ua.indexOf("opera") !== -1 || ua.indexOf("opr") !== -1) return "Opera";

  return "unknown";
};

/**
 * Extract Electron version from user agent
 */
const getElectronVersion = (userAgent: string): string | undefined => {
  const match = userAgent.match(/Electron\/([\d.]+)/);
  return match ? match[1] : undefined;
};
